"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@src/lib/utils/cn";
import { MODAL_MOTION_CLASSNAME } from "@src/lib/ui/dialogShell";
import { Button } from "@src/lib/ui/Button";
import { ButtonGroup } from "@src/lib/ui/ButtonGroup";
import PlusIcon from "@src/lib/icons/18px/plus";
import MinusIcon from "@src/lib/icons/18px/minus";
import XIcon from "@src/lib/icons/12px/x";
import {
  MediaGallery,
  type MediaGalleryItem,
  type MediaGalleryRenderContext,
} from "@src/lib/ui/MediaGallery";

export interface MediaGalleryDialogProps {
  items: MediaGalleryItem[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Merged onto the overlay and panel, e.g. a `z-*` to open above a raised dialog. */
  className?: string;
  showCounter?: boolean;
  /** Lays out the header row; `controls` is the zoom and close buttons. */
  renderHeader?: (
    ctx: MediaGalleryRenderContext,
    controls: React.ReactNode,
  ) => React.ReactNode;
}

export function MediaGalleryDialog({
  items,
  initialIndex = 0,
  open,
  onOpenChange,
  className,
  showCounter = true,
  renderHeader,
}: MediaGalleryDialogProps): React.ReactElement {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Full-screen gallery: fade only (no scale) on the modal motion timing. */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80",
            MODAL_MOTION_CLASSNAME,
            className,
          )}
        />
        <DialogPrimitive.Content
          onEscapeKeyDown={(event) => event.stopPropagation()}
          className={cn(
            // A fixed dark stage in both themes; there's no semantic token
            // for it yet (see ## Media in design.md).
            "fixed inset-0 z-50 flex items-center justify-center bg-[#1a1a1a] focus-visible:outline-none",
            MODAL_MOTION_CLASSNAME,
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Media gallery
          </DialogPrimitive.Title>

          <MediaGallery
            items={items}
            initialIndex={initialIndex}
            active={open}
            isolateKeyboard
            showCounter={showCounter}
            mediaClassName="h-dvh w-[100dvh] object-contain"
            header={(ctx) => {
              const { mode, canZoom, zoomIn, resetZoom } = ctx;
              const controls = (
                <div className="pointer-events-auto flex items-center justify-end gap-2">
                  {/* Zoom button group — images only */}
                  {canZoom && (
                    <ButtonGroup>
                      <Button
                        variant="blur"
                        size="icon"
                        onClick={zoomIn}
                        aria-label="Zoom in"
                        className={cn(mode === "zoom" && "bg-white/15")}
                      >
                        <PlusIcon />
                      </Button>
                      <Button
                        variant="blur"
                        size="icon"
                        onClick={resetZoom}
                        aria-label="Zoom out / reset"
                        className={cn(mode === "view" && "bg-white/15")}
                      >
                        <MinusIcon />
                      </Button>
                    </ButtonGroup>
                  )}

                  {/* Close button */}
                  <DialogPrimitive.Close asChild>
                    <Button
                      variant="blur"
                      size="icon"
                      aria-label="Close gallery"
                    >
                      <XIcon />
                    </Button>
                  </DialogPrimitive.Close>
                </div>
              );
              return renderHeader ? renderHeader(ctx, controls) : controls;
            }}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
